/**
 * CDN image URL helpers for TrafiFlow store CDN resize params.
 *
 * The shared CDN image pipeline rewrites `?width=&height=&format=` into a
 * derived S3 key (e.g. `/media/…/avif/800xauto/x.jpg.avif`). When `format` is
 * omitted the edge negotiates from `Accept` (avif → webp → jpeg).
 * Non-CDN hosts (Shopify, etc.) pass through unchanged.
 */

export type CdnImageFormat = 'webp' | 'avif' | 'jpeg' | 'png';

export interface CdnImageOpts {
  width?: number;
  height?: number;
  /** Optional override. Omit to let the CDN negotiate from Accept. */
  format?: CdnImageFormat;
  /**
   * Optional quality override (1–100). Edge snaps to 60/75/85/95.
   * Omit to use per-format defaults (jpeg 85, webp 80, avif 60).
   */
  quality?: number;
}

const DEFAULT_CDN_HOST_PATTERN =
  /^cdn\.[a-z0-9-]+\.(dev|prod|stage)\.trafiflow\.com$/i;
const LEGACY_CDN_HOST_PATTERN = /^cdn\.(dev|prod)\.trafiflow\.com$/i;

/**
 * Shared breakpoint set — must stay in sync with the CDN url-encoder
 * (`lambdas/cdn-url-encoder` BREAKPOINTS). Arbitrary widths are snapped at the edge.
 */
export const DEFAULT_SRCSET_WIDTHS = [400, 800, 1200, 1600] as const;

/** Hero / LCP desktop srcSet widths (CDN encoder breakpoints). */
export const HERO_DESKTOP_SRCSET_WIDTHS = [1280, 1600, 1920] as const;

/** Hero / LCP mobile srcSet widths (CDN encoder breakpoints). */
export const HERO_MOBILE_SRCSET_WIDTHS = [640, 960, 1280] as const;

export const DEFAULT_IMAGE_SIZES =
  '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';

function parseHostList(raw: string | undefined | null): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
}

/**
 * True when `src` points at a TrafiFlow CDN host that supports resize params.
 * `configuredHosts` comes from the host app (`VITE_CDN_HOSTS` / `env.CDN_HOSTS`);
 * otherwise falls back to the standard `cdn.{store}.{env}.trafiflow.com` pattern.
 */
export function isCdnResizable(
  src: string,
  configuredHosts: string[] | string = [],
): boolean {
  const hosts = Array.isArray(configuredHosts)
    ? configuredHosts.map((h) => h.toLowerCase())
    : parseHostList(configuredHosts);

  let hostname: string;
  try {
    hostname = new URL(src).hostname.toLowerCase();
  } catch {
    return false;
  }

  if (hosts.some((h) => hostname === h || hostname.endsWith(`.${h}`))) {
    return true;
  }

  return (
    DEFAULT_CDN_HOST_PATTERN.test(hostname) ||
    LEGACY_CDN_HOST_PATTERN.test(hostname)
  );
}

/**
 * Append (or replace) resize query params on a CDN URL.
 * Non-resizable URLs are returned unchanged.
 */
export function buildCdnImageUrl(
  src: string,
  opts: CdnImageOpts = {},
  configuredHosts: string[] | string = [],
): string {
  const trimmed = src?.trim() ?? '';
  if (!trimmed) return '';
  if (!isCdnResizable(trimmed, configuredHosts)) return trimmed;

  try {
    const url = new URL(trimmed);
    if (opts.width != null && opts.width > 0) {
      url.searchParams.set('width', String(Math.round(opts.width)));
    }
    if (opts.height != null && opts.height > 0) {
      url.searchParams.set('height', String(Math.round(opts.height)));
    }
    if (opts.format) {
      url.searchParams.set('format', opts.format);
    }
    if (opts.quality != null && opts.quality > 0 && opts.quality <= 100) {
      url.searchParams.set('quality', String(Math.round(opts.quality)));
    }
    return url.toString();
  } catch {
    return trimmed;
  }
}

/**
 * Build a comma-separated srcSet for the given widths.
 * Returns empty string when the source is not CDN-resizable (caller should omit srcSet).
 *
 * By default omits `format` so the CDN negotiates from Accept.
 */
export function buildSrcSet(
  src: string,
  widths: readonly number[] = DEFAULT_SRCSET_WIDTHS,
  format?: CdnImageFormat,
  configuredHosts: string[] | string = [],
): string {
  if (!isCdnResizable(src, configuredHosts)) return '';

  return widths
    .filter((w) => w > 0)
    .map((w) => {
      const opts: CdnImageOpts = { width: w };
      if (format) opts.format = format;
      return `${buildCdnImageUrl(src, opts, configuredHosts)} ${w}w`;
    })
    .join(', ');
}
